<script setup lang="ts">
import { GraphicFieldType, TextFieldType } from "@regulaforensics/document-reader-webclient";
import { computed, ref } from "vue";

import WIDGET_ANALYTICS_EVENTS from "@/helpers/widgetAnalyticsEvents/widgetAnalyticsEvents";
import regulaScanDocument from "@/hooks/requests/regulaScanDocument/regulaScanDocument";
import { useAnalytics } from "@/hooks/useAnalytics/useAnalytics";
import { useTransitionHeight } from "@/hooks/useTransitionHeight/useTransitionHeight";
import useTranslate from "@/hooks/useTranslate/useTranslate";
import { useImageFullScreenStore } from "@/stores/imageFullScreen/imageFullScreen";
import { useTIWStore } from "@/stores/tiw/tiw";
import { useVerificationStore } from "@/stores/verification/verification";
import Button from "@/ui/button/button.vue";
import ArrowIcon from "@/ui/icons/arrow.vue";

import type { IFileData, Side } from "./documentReader";

import Modal from "../../modal/modal.vue";
import { convertFiles } from "./documentReader";

const pathToImages = import.meta.env.VITE_APP_URL;

const TIWStore = useTIWStore();
const ImageFullScreenStore = useImageFullScreenStore();
const VerificationStore = useVerificationStore();

const guideTransition = useTransitionHeight(500);

const isLoading = ref<boolean>(false);
const uploadedFiles = ref<IFileData[]>([]);
const isGuide = ref<boolean>(true);
const fileInputFrontEl = ref<HTMLInputElement>();
const fileInputBackEl = ref<HTMLInputElement>();
const errorsUploading = ref<{ side: Side; message: string } | null>(null);

const uploadedFront = computed(() => {
    const found = uploadedFiles.value.find((el) => el.side === "front");
    return found;
});

const uploadedBack = computed(() => {
    const found = uploadedFiles.value.find((el) => el.side === "back");
    return found;
});

function onBack() {
    TIWStore.switchStep("personAuthentication");
}

function removeUploadedImage(id: string) {
    uploadedFiles.value = uploadedFiles.value.filter((el) => el.id !== id);
}
async function scanDocuments() {
    isLoading.value = true;
    // oxlint-disable-next-line
    const result = await regulaScanDocument([...uploadedFiles.value.map((el) => el.data)]).finally(
        () => {
            isLoading.value = false;
            uploadedFiles.value = [];
        },
    );

    const documentResult = result?.status.overallStatus;

    const data = {
        name: result?.text?.getFieldValue(TextFieldType.GIVEN_NAMES),
        lastName: result?.text?.getFieldValue(TextFieldType.SURNAME),
        sex: result?.text?.getFieldValue(TextFieldType.SEX),
        dateOfBirth: result?.text?.getFieldValue(TextFieldType.DATE_OF_BIRTH),
        placeOfBirth: result?.text?.getFieldValue(TextFieldType.PLACE_OF_BIRTH),
        issuingState: result?.text?.getFieldValue(TextFieldType.ISSUING_STATE_NAME),
        nationality: result?.text?.getFieldValue(TextFieldType.NATIONALITY),
        id: result?.text?.getFieldValue(TextFieldType.DOCUMENT_NUMBER),
        nationalityCode: result?.text?.getFieldValue(TextFieldType.NATIONALITY_CODE),
        nameOfTheIssuingAuthority: result?.text?.getFieldValue(TextFieldType.AUTHORITY),
        dateOfIssue: result?.text?.getFieldValue(TextFieldType.DATE_OF_ISSUE),
        dateOfExpiry: result?.text?.getFieldValue(TextFieldType.DATE_OF_EXPIRY),
        signature: result?.images?.getField(GraphicFieldType.SIGNATURE)?.valueList[0]?.value,
        face: result?.images?.getField(GraphicFieldType.PORTRAIT)?.valueList[0]?.value,
        card: result?.images?.getField(GraphicFieldType.DOCUMENT_FRONT)?.valueList[0]?.value,
        cardBack: result?.images?.getField(GraphicFieldType.DOCUMENT_FRONT)?.valueList[1]?.value,
        pesel: result?.text?.getFieldValue(TextFieldType.PERSONAL_NUMBER),
        age: result?.text?.getFieldValue(TextFieldType.AGE),
    };

    const keys = Object.keys(data);

    const isValid = keys.every((key) => {
        if (key === "cardBack" || key === "card") {
            return true;
        }
        return data[key as keyof typeof data] !== undefined;
    });

    if (isValid && documentResult === 1) {
        TIWStore.switchStep("personAuthenticationSuccess");
        VerificationStore.verificationData = data;
        return;
    }

    TIWStore.switchStep("personAuthenticationFailed");
}
function resetFields(side: Side) {
    const fieldToReset = side === "front" ? fileInputFrontEl : fileInputBackEl;
    if (fieldToReset?.value?.value) {
        fieldToReset.value.value = null;
    }
}

async function uploadFiles(files: FileList, side: Side) {
    errorsUploading.value = null;
    if (files.length) {
        isLoading.value = true;
        if (files[0].size > 5000000 /* 5mb */) {
            errorsUploading.value = { side, message: useTranslate("tiw__upload__error__size") };
            resetFields(side);
            return;
        }
        const convertedFiles = await convertFiles(files);
        const convertedFilesWithSide = convertedFiles.map((el) => {
            return { ...el, side };
        });

        uploadedFiles.value.push(...convertedFilesWithSide);
        resetFields(side);

        useAnalytics(WIDGET_ANALYTICS_EVENTS.BIOMETRIC_VERIFICATION_UPLOAD);
        isLoading.value = false;
    }
}

function handlerIsGuide() {
    isGuide.value = !isGuide.value;
}

function onClickImagePreview(img: string) {
    ImageFullScreenStore.setImageUrl(img);
    ImageFullScreenStore.show();
}

const documentPreparationGuide = [
    useTranslate("tiw__person__upload__documentGuide__1"),
    useTranslate("tiw__person__upload__documentGuide__2"),
    useTranslate("tiw__person__upload__documentGuide__3"),
    useTranslate("tiw__person__upload__documentGuide__4"),
    useTranslate("tiw__person__upload__documentGuide__5"),
    useTranslate("tiw__person__upload__documentGuide__6"),
    useTranslate("tiw__person__upload__documentGuide__7"),
    useTranslate("tiw__person__upload__documentGuide__8"),
    useTranslate("tiw__person__upload__documentGuide__9"),
    useTranslate("tiw__person__upload__documentGuide__10"),
    useTranslate("tiw__person__upload__documentGuide__11"),
];

const howToUploadDocuments = [
    useTranslate("tiw__person__upload__howToUpload__1"),
    useTranslate("tiw__person__upload__howToUpload__2"),
    useTranslate("tiw__person__upload__howToUpload__3"),
    useTranslate("tiw__person__upload__howToUpload__4"),
    useTranslate("tiw__person__upload__howToUpload__5"),
];
</script>

<template>
    <Modal class="tiw__documentUpload">
        <template #header>
            <p class="tiw__h2">
                {{ useTranslate("tiw__person__upload__title") }}
            </p>
        </template>
        <div class="tiw__documentUpload__docPreparation__title">
            <button @click="handlerIsGuide">
                <p>
                    {{ useTranslate("tiw__person__upload__documentGuide") }}
                </p>
                <svg
                    :class="
                        isGuide ? '' : 'tiw__documentUpload__docPreparation__title__arrowRotate'
                    "
                    width="16"
                    height="9"
                    viewBox="0 0 16 9"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M0.744078 8.75592C1.06951 9.08136 1.59715 9.08136 1.92259 8.75592L8 2.67851L14.0774 8.75592C14.4028 9.08136 14.9305 9.08136 15.2559 8.75592C15.5814 8.43049 15.5814 7.90285 15.2559 7.57741L8.58926 0.910744C8.26382 0.585307 7.73618 0.585307 7.41074 0.910744L0.744078 7.57741C0.418641 7.90285 0.418641 8.43049 0.744078 8.75592Z"
                        fill="black"
                    />
                </svg>
            </button>
        </div>
        <Transition v-on="guideTransition">
            <div v-show="isGuide" class="tiw__documentUpload__docPreparation__container">
                <ul class="tiw__documentUpload__docPreparation__container__guideList">
                    <li v-for="(value, i) in documentPreparationGuide" :key="`guide-${i}`">
                        {{ value }}
                    </li>
                </ul>
                <p class="tiw__documentUpload__docPreparation__container__howToUpload__title">
                    {{ useTranslate("tiw__person__upload__howToUpload") }}
                </p>
                <div class="tiw__documentUpload__docPreparation__container__howToUpload__list">
                    <div
                        v-for="(value, i) in howToUploadDocuments"
                        :key="`howToUpload-${i}`"
                        class="tiw__documentUpload__docPreparation__container__howToUpload__list__item"
                    >
                        <p>
                            <b>{{ i + 1 }}</b>
                        </p>
                        <p>{{ value }}</p>
                    </div>
                </div>
                <p class="tiw__documentUpload__docPreparation__container__docExamples__title">
                    {{ useTranslate("tiw__person__upload__examples") }}
                </p>
                <div class="tiw__documentUpload__docPreparation__container__docExamples__body">
                    <img
                        width="100%"
                        :src="`${pathToImages}/img/guide_section/document_example.webp`"
                        alt="document"
                    />
                    <img
                        width="100%"
                        :src="`${pathToImages}/img/guide_section/document_example_2.webp`"
                        alt="document"
                    />
                </div>
            </div>
        </Transition>
        <div
            v-if="!uploadedFiles.some((el) => el.side === 'front')"
            class="tiw__documentUpload__fileUpload__body"
        >
            <img :src="`${pathToImages}/img/guide_section/id_icon.svg`" alt="id icon" />
            <div class="tiw__documentUpload__fileUpload__body__field">
                <p class="tiw__documentUpload__fileUpload__body__field__title">
                    <b>{{ useTranslate("tiw__person__upload__documentImageFront") }}</b>
                </p>
                <p
                    v-if="errorsUploading?.side === 'front'"
                    class="tiw__documentUpload__fileUpload__body__field__error"
                >
                    {{ errorsUploading.message }}
                </p>
                <label class="tiw__documentUpload__fileUpload__body__button" for="fileInputFront">
                    {{ useTranslate("tiw__person__upload__image") }}
                </label>
                <input
                    id="fileInputFront"
                    ref="fileInputFrontEl"
                    accept="image/*, application/pdf"
                    hidden
                    class="tiw__documentUpload__fileUpload__body__field__input"
                    type="file"
                    @change="uploadFiles(fileInputFrontEl.files, 'front')"
                />
            </div>
        </div>
        <div v-else class="tiw__documentUpload__fileUpload__uploaded__item">
            <button
                class="tiw__documentUpload__fileUpload__uploaded__item__openImage"
                @click="onClickImagePreview(uploadedFront.base64)"
            >
                <img
                    :width="uploadedFront.width"
                    :height="uploadedFront.height"
                    class="tiw__documentUpload__fileUpload__uploaded__item__img"
                    :src="uploadedFront.base64"
                    alt="uploaded image"
                />
            </button>
            <div class="tiw__documentUpload__fileUpload__uploaded__item__actions">
                <p>{{ useTranslate("tiw__person__upload__documentImage") }}</p>
                <div class="tiw__documentUpload__fileUpload__uploaded__item__actions__content">
                    <div class="tiw__documentUpload__fileUpload__uploaded__item__actions__name">
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <rect
                                x="0.75"
                                y="0.75"
                                width="14.5"
                                height="14.5"
                                rx="7.25"
                                stroke="#00C853"
                                stroke-width="1.5"
                            />
                            <path
                                d="M5.33398 8.10449L7.2196 9.99011L10.9908 6.21887"
                                stroke="#00C853"
                                stroke-width="1.5"
                                stroke-linecap="round"
                            />
                        </svg>
                        <p>{{ uploadedFront.name }}</p>
                    </div>
                    <button
                        class="tiw__documentUpload__fileUpload__uploaded__remove"
                        @click="removeUploadedImage(uploadedFront.id)"
                    >
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M2.5 5H4.16667H17.5"
                                stroke="#F14459"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                            <path
                                d="M6.66602 5.00033V3.33366C6.66602 2.89163 6.84161 2.46771 7.15417 2.15515C7.46673 1.84259 7.89065 1.66699 8.33268 1.66699H11.666C12.108 1.66699 12.532 1.84259 12.8445 2.15515C13.1571 2.46771 13.3327 2.89163 13.3327 3.33366V5.00033M15.8327 5.00033V16.667C15.8327 17.109 15.6571 17.5329 15.3445 17.8455C15.032 18.1581 14.608 18.3337 14.166 18.3337H5.83268C5.39065 18.3337 4.96673 18.1581 4.65417 17.8455C4.34161 17.5329 4.16602 17.109 4.16602 16.667V5.00033H15.8327Z"
                                stroke="#F14459"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                            <path
                                d="M8.33398 9.16699V14.167"
                                stroke="#F14459"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                            <path
                                d="M11.666 9.16699V14.167"
                                stroke="#F14459"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
        <hr
            v-if="!uploadedFiles.length"
            color="#D3DCE9"
            size="1"
            class="tiw__documentUpload__fileUpload__line"
        />
        <div
            v-if="!uploadedFiles.some((el) => el.side === 'back')"
            class="tiw__documentUpload__fileUpload__body"
        >
            <img :src="`${pathToImages}/img/guide_section/ic_icon.svg`" alt="id icon" />
            <div class="tiw__documentUpload__fileUpload__body__field">
                <p class="tiw__documentUpload__fileUpload__body__field__title">
                    <b>{{ useTranslate("tiw__person__upload__documentImageBack") }}</b>
                </p>
                <p
                    v-if="errorsUploading?.side === 'back'"
                    class="tiw__documentUpload__fileUpload__body__field__error"
                >
                    {{ errorsUploading.message }}
                </p>
                <label class="tiw__documentUpload__fileUpload__body__button" for="fileInputBack">
                    {{ useTranslate("tiw__person__upload__image") }}
                </label>
                <input
                    id="fileInputBack"
                    ref="fileInputBackEl"
                    accept="image/*, application/pdf"
                    hidden
                    class="tiw__documentUpload__fileUpload__body__field__input"
                    type="file"
                    @change="uploadFiles(fileInputBackEl.files, 'back')"
                />
            </div>
        </div>
        <div v-else class="tiw__documentUpload__fileUpload__uploaded__item">
            <button
                class="tiw__documentUpload__fileUpload__uploaded__item__openImage"
                @click="onClickImagePreview(uploadedBack.base64)"
            >
                <img
                    :width="uploadedBack.width"
                    :height="uploadedBack.height"
                    class="tiw__documentUpload__fileUpload__uploaded__item__img"
                    :src="uploadedBack.base64"
                    alt="uploaded image"
                />
            </button>
            <div class="tiw__documentUpload__fileUpload__uploaded__item__actions">
                <p>{{ useTranslate("tiw__person__upload__documentImage") }}</p>
                <div class="tiw__documentUpload__fileUpload__uploaded__item__actions__content">
                    <div class="tiw__documentUpload__fileUpload__uploaded__item__actions__name">
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <rect
                                x="0.75"
                                y="0.75"
                                width="14.5"
                                height="14.5"
                                rx="7.25"
                                stroke="#00C853"
                                stroke-width="1.5"
                            />
                            <path
                                d="M5.33398 8.10449L7.2196 9.99011L10.9908 6.21887"
                                stroke="#00C853"
                                stroke-width="1.5"
                                stroke-linecap="round"
                            />
                        </svg>
                        <p>{{ uploadedBack.name }}</p>
                    </div>
                    <button
                        class="tiw__documentUpload__fileUpload__uploaded__remove"
                        @click="removeUploadedImage(uploadedBack.id)"
                    >
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M2.5 5H4.16667H17.5"
                                stroke="#F14459"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                            <path
                                d="M6.66602 5.00033V3.33366C6.66602 2.89163 6.84161 2.46771 7.15417 2.15515C7.46673 1.84259 7.89065 1.66699 8.33268 1.66699H11.666C12.108 1.66699 12.532 1.84259 12.8445 2.15515C13.1571 2.46771 13.3327 2.89163 13.3327 3.33366V5.00033M15.8327 5.00033V16.667C15.8327 17.109 15.6571 17.5329 15.3445 17.8455C15.032 18.1581 14.608 18.3337 14.166 18.3337H5.83268C5.39065 18.3337 4.96673 18.1581 4.65417 17.8455C4.34161 17.5329 4.16602 17.109 4.16602 16.667V5.00033H15.8327Z"
                                stroke="#F14459"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                            <path
                                d="M8.33398 9.16699V14.167"
                                stroke="#F14459"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                            <path
                                d="M11.666 9.16699V14.167"
                                stroke="#F14459"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
        <div class="tiw__documentUpload__fileUpload__uploaded">
            <div class="tiw__documentUpload__action">
                <Button
                    type="button"
                    variant="skeleton"
                    class="tiw__step__chooseSigningType__btn-back"
                    @click="onBack"
                >
                    <ArrowIcon />
                    <span>{{ useTranslate("tiw__back") }}</span>
                </Button>
                <Button :disabled="isLoading || uploadedFiles.length < 2" @click="scanDocuments">
                    {{ useTranslate("tiw__continue") }}
                </Button>
            </div>
        </div>
    </Modal>
</template>

<style lang="scss" src="./documentsUpload.scss"></style>
